import { apiGet, apiPost, apiPut, apiDelete } from '@/services/api/apiClient';

export interface Material {
  _id: string;
  materialName: string;
  companyId?: string | null;
  materialType: string;
  quantityCount: number;
  quantityKg: number;
  initialCount: number;
  initialKg: number;
  description: string;
  arrivedDateTime: string;
  vehicleNumber: string;
  supplierId?: string | null;
  jobType?: string;
  isActive: boolean;
  isDeleted: boolean;
  remarks?: string;
  createdAt: string;
  updatedAt: string;
}

export interface MaterialFormData {
  materialName: string;
  companyId?: string;
  materialType: string;
  quantityCount: number;
  quantityKg: number;
  description: string;
  arrivedDateTime: string;
  vehicleNumber: string;
  supplierId?: string;
  isActive: boolean;
  remarks?: string;
  jobType?: string;
}

export interface BulkMaterialItem {
  materialId?: string;
  materialName?: string;
  materialType?: string;
  quantityCount: number;
  quantityKg: number;
}

export interface BulkMaterialFormData {
  companyId: string;
  arrivedDateTime: string;
  vehicleNumber: string;
  supplierId: string;
  documentNumber?: string;
  jobType: string;
  remarks?: string;
  materials: BulkMaterialItem[];
}

export interface MaterialListParams {
  page?: number;
  limit?: number;
  search?: string;
  isActive?: boolean;
}

export interface MaterialsListParams {
  page?: number;
  limit?: number;
  companyId: string;
  supplierId: string;
  jobType?: string;
}

export interface MaterialsByGstParams {
  gst: string;
  page?: number;
  limit?: number;
  search?: string;
  companyId: string;
  isActive?: boolean;
  jobType?: string;
}

export interface MaterialListResponse {
  success: boolean;
  message: string;
  data: {
    docs: Material[];
    totalDocs: number;
    limit: number;
    totalPages: number;
    page: number;
    pagingCounter: number;
    hasPrevPage: boolean;
    hasNextPage: boolean;
    prevPage: number | null;
    nextPage: number | null;
  };
}

export interface MaterialResponse {
  success: boolean;
  message: string;
  data: Material;
}

class MaterialService {
  private baseUrl = '/materials';

  async getMaterials(params: MaterialListParams = {}): Promise<MaterialListResponse> {
    const queryParams = new URLSearchParams();
    
    if (params.page) queryParams.append('page', params.page.toString());
    if (params.limit) queryParams.append('limit', params.limit.toString());
    if (params.search) queryParams.append('search', params.search);
    if (params.isActive !== undefined) queryParams.append('isActive', params.isActive.toString());

    const url = `${this.baseUrl}${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
    return apiGet<MaterialListResponse>(url);
  }

  async createMaterial(data: MaterialFormData): Promise<MaterialResponse> {
    return apiPost<MaterialResponse>(this.baseUrl, data);
  }

  async createBulkMaterials(data: BulkMaterialFormData): Promise<MaterialResponse> {
    return apiPost<MaterialResponse>(this.baseUrl, data);
  }

  async updateMaterial(id: string, data: MaterialFormData): Promise<MaterialResponse> {
    return apiPut<MaterialResponse>(`${this.baseUrl}/${id}`, data);
  }

  async deleteMaterial(id: string): Promise<{ success: boolean; message: string }> {
    return apiDelete<{ success: boolean; message: string }>(`${this.baseUrl}/${id}`);
  }

  async getMaterialById(id: string): Promise<MaterialResponse> {
    return apiGet<MaterialResponse>(`${this.baseUrl}/${id}`);
  }

  async getMaterialsList(params: MaterialsListParams): Promise<MaterialListResponse> {
    const queryParams = new URLSearchParams();

    if (params.page) queryParams.append('page', params.page.toString());
    if (params.limit) queryParams.append('limit', params.limit.toString());
    queryParams.append('companyId', params.companyId);
    queryParams.append('supplierId', params.supplierId);
    if (params.jobType) queryParams.append('jobType', params.jobType);

    const url = `${this.baseUrl}/list?${queryParams.toString()}`;
    return apiGet<MaterialListResponse>(url);
  }

  async getMaterialsByGst(params: MaterialsByGstParams): Promise<MaterialListResponse> {
    const queryParams = new URLSearchParams();

    queryParams.append('gst', params.gst);
    queryParams.append('companyId', params.companyId);
    if (params.page) queryParams.append('page', params.page.toString());
    if (params.limit) queryParams.append('limit', params.limit.toString());
    if (params.search) queryParams.append('search', params.search);
    if (params.isActive !== undefined) queryParams.append('isActive', params.isActive.toString());
    if (params.jobType) queryParams.append('jobType', params.jobType);

    const url = `${this.baseUrl}/by-gst?${queryParams.toString()}`;
    return apiGet<MaterialListResponse>(url);
  }
}

export const materialService = new MaterialService();